import { type LoadingManager, type Texture, Color, FrontSide, MeshPhysicalMaterial, MeshStandardMaterial, SRGBColorSpace, TextureLoader } from 'three';
import { ScreenLensMaterial } from './LensOptics';
import type { FrameLook } from '../config/frames';

/**
 * Materials of the procedural / untextured frames.
 *
 * Acetate is a thick, polished, slightly translucent plastic: a glossy
 * clearcoat over a base colour that lets a little of what is behind show
 * through. Without a transmission pass (the camera image lives in the DOM, not
 * in the scene) translucency is approximated with a light transparency — the
 * skin and the temples show faintly through the front — and a faint self-glow
 * that keeps the shadowed side from going black, as scattered light does in
 * real acetate.
 */
export function createAcetateMaterial(look: FrameLook): MeshPhysicalMaterial {
  const base = new Color(look.acetateColor);
  const mat = new MeshPhysicalMaterial({
    color: base,
    roughness: look.acetateRoughness,
    metalness: 0,
    clearcoat: 1.0,
    clearcoatRoughness: 0.1,
    ior: 1.49,
    specularIntensity: 0.5, // the clearcoat is the polished surface (same as the photo material)
    specularColor: tintedSpecular(look.acetateColor),
    envMapIntensity: 0.7,
    emissive: base.clone().multiplyScalar(0.04),
    transparent: look.acetateOpacity < 1,
    opacity: look.acetateOpacity,
    side: FrontSide,
  });
  return mat;
}

/** Absorption/reflection from the composed camera scene, with alpha fallback for transparent exports. */
export function createLensMaterial(look: FrameLook): MeshPhysicalMaterial {
  return new ScreenLensMaterial(look);
}

/**
 * Acetate carrying the real product photo as albedo: the exact colour, the
 * translucency, the rivets and the core wire come from the pixels, while the
 * clearcoat still puts live highlights on top that move with the head. The
 * photo is a lit studio shot: the room lighting is calibrated so that, on a
 * face exposed at the reference luminance, a photo pixel renders at about its
 * own brightness (slightly under: a white sweep is brighter than a room), and
 * the base specular is halved so the clearcoat is the one polished surface
 * rather than two stacked ones.
 */
export function loadPhotoTexture(dataUrl: string, manager?: LoadingManager): Texture {
  const map = new TextureLoader(manager).load(dataUrl);
  map.colorSpace = SRGBColorSpace;
  map.anisotropy = 4;
  return map;
}

/**
 * A110Y: one texture per photo file for a whole build. `createPhotoAcetateMaterial` used to load
 * its photo itself: the temple photo (up to 3970×810, 16 MB with mipmaps) was decoded and
 * uploaded twice per frame, the front photo up to three times (front, rear plate, raised rims).
 */
export class PhotoTextures {
  private readonly cache = new Map<string, Texture>();
  constructor(private readonly manager?: LoadingManager) {}
  get(dataUrl: string): Texture {
    let t = this.cache.get(dataUrl);
    if (!t) {
      t = loadPhotoTexture(dataUrl, this.manager);
      this.cache.set(dataUrl, t);
    }
    return t;
  }
}

export function createPhotoAcetateMaterial(photo: string | Texture, roughness: number, acetateColor: string, opacity = 0.97, manager?: LoadingManager, uniformBelowUv?: [number, number]): MeshPhysicalMaterial {
  const map = typeof photo === 'string' ? loadPhotoTexture(photo, manager) : photo;
  const mat = new MeshPhysicalMaterial({
    map,
    color: new Color('#eef4ee'),
    roughness,
    metalness: 0,
    clearcoat: 1.0,
    // Polished acetate: small, sharp highlights (a broad soft sheen reads as plastic).
    clearcoatRoughness: 0.08,
    ior: 1.49,
    specularIntensity: 0.5,
    // Coloured acetate is translucent: what its body reflects from under the
    // clearcoat carries its colour. A white sheen greys the olive out; the
    // clearcoat on top stays white.
    specularColor: tintedSpecular(acetateColor),
    envMapIntensity: 0.7,
    // Per-colorway visual translucency. This is alpha compositing, not a
    // volumetric measurement: retain dense colour and live clearcoat highlights.
    transparent: opacity < 1,
    opacity,
    side: FrontSide,
  });
  if (uniformBelowUv) {
    // A photographed white rim can contain a fixed studio reflection which
    // looks like a second edge. Replace only that uniform lower albedo; keep
    // the real upper photo/rivets and all live clearcoat/lighting responses.
    const [high, low] = uniformBelowUv;
    mat.onBeforeCompile = shader => {
      shader.uniforms.uniformLowerAlbedo = { value: new Color(acetateColor) };
      shader.uniforms.uniformLowerHigh = { value: high };
      shader.uniforms.uniformLowerLow = { value: low };
      shader.fragmentShader = 'uniform vec3 uniformLowerAlbedo;\nuniform float uniformLowerHigh;\nuniform float uniformLowerLow;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `
        #include <map_fragment>
        #ifdef USE_MAP
          float lowerWeight = 1.0 - smoothstep(uniformLowerLow, uniformLowerHigh, vMapUv.y);
          diffuseColor.rgb = mix(diffuseColor.rgb, diffuse * uniformLowerAlbedo, lowerWeight);
        #endif
      `);
    };
    mat.customProgramCacheKey = () => 'uniform-lower-photo-albedo-v1';
  }
  return mat;
}

/** Specular tint of a coloured translucent plastic: its own colour, well lightened. */
function tintedSpecular(hex: string): Color {
  return new Color(hex).lerp(new Color(1, 1, 1), 0.35);
}

/** Hinge rivets / pins: dark gunmetal. */
export function createRivetMaterial(): MeshStandardMaterial {
  return new MeshStandardMaterial({ color: new Color('#2b3033'), metalness: 0.9, roughness: 0.35, envMapIntensity: 1.2 });
}

/** Tags a material so the fade logic knows its resting opacity. */
export function rememberBaseOpacity(mat: MeshPhysicalMaterial): void {
  mat.userData.baseOpacity = mat.opacity;
  mat.userData.baseTransparent = mat.transparent;
}
